import { useMemo } from 'react';

/**
 * Computes a password strength value for a provided password and zxcvbnScore. The maximum value is 6. The user gets up
 * to 3 points for password length and up to 3 points for having each of the different optional categories of
 * characters. The sum of the score is multiplied by the score computed from zxcvbn (divided by the max zxcvbn score of
 * 4) which checks for common passwords and easily guessable combinations.
 *
 * @param zxcvbnScore the score returned from evaluating password with zxcvbn()
 * @param password the password to evaluate
 */
export function usePasswordStrength(zxcvbnScore: number, password?: string) {
  return useMemo(() => {
    let value = 0;
    if ((password?.length ?? 0) >= 15) {
      value += 3;
    } else if ((password?.length ?? 0) >= 12) {
      value += 2;
    } else if ((password?.length ?? 0) >= 8) {
      value += 1;
    }

    let matches = 0;

    if (password?.match(/[a-z]/)) {
      matches += 1;
    }
    if (password?.match(/[A-Z]/)) {
      matches += 1;
    }
    if (password?.match(/[0-9]/)) {
      matches += 1;
    }
    if (password?.match(/[^a-zA-Z0-9]/)) {
      matches += 1;
    }

    // We only want to give a point value of up to 3 for matches, but no less than 0.
    value += Math.max(0, matches - 1);

    const passwordStrength = value * (zxcvbnScore / 4);

    // If the user hasn't met the minimum requirements for password (at least 3 match categories and a length of 8, then
    // the max score they can reach is 1.5.
    return matches < 2 || (password?.length ?? 0) < 8 ? Math.min(passwordStrength, 1.5) : passwordStrength;
  }, [password, zxcvbnScore]);
}
